import { createApi } from '@reduxjs/toolkit/query/react';
import fetchBaseQueryCSRF from '../helper';
import {
  NonBrandKpisResponse,
  NonBrandMetricEvolutionResponse,
  NonBrandTrafficTimeSeriesResponse,
  NonBrandSearchTermOption,
  SearchTermsGridResponse,
  ClicksAdCostsResponse,
  VisibilityResponse,
  TopCompetitorsResponse,
  RegionsResponse,
  CompetitorsResponse,
} from './models';
import {
  PaginateQuery,
  CompetitorsFiltersQuery,
  SearchTermsFiltersQuery,
  SearchTermsFiltersQueryWithSearchTerms,
} from './query';
import { GridFilterModel } from '@mui/x-data-grid';

/** Repeated filter params (`&` in country names sent as `__et__`), clusters as one comma-joined `tags`. */
export const buildFilterQueryString = ({
  countries = [],
  devices = [],
  platforms = [],
  search_terms = [],
  tags = [],
}: {
  countries?: string[];
  devices?: string[];
  platforms?: string[];
  search_terms?: string[];
  tags?: string[];
}): string => {
  const params = new URLSearchParams();
  countries.forEach((country) => params.append('countries', country.replace('&', '__et__')));
  devices.forEach((device) => params.append('devices', device));
  platforms.forEach((platform) => params.append('platforms', platform));
  search_terms.forEach((term) => params.append('search_terms', term));
  if (tags.length > 0) params.append('tags', tags.join(','));
  return params.toString();
};

/** Args of the page-level Non Brand Search Performance queries (KPI cards, charts). */
export type NonBrandPerformanceArgs = SearchTermsFiltersQueryWithSearchTerms & {
  start_compare?: string;
  end_compare?: string;
  compare?: boolean;
  /** Header opportunity pill (grid opportunity key), null = every search term. */
  opportunity?: string | null;
};

/** Filters as POST body fields: lists as arrays, clusters as one comma-joined `tags` (the API's format). */
const buildFilterBody = ({
  countries = [],
  devices = [],
  platforms = [],
  search_terms = [],
  tags = [],
}: {
  countries?: string[];
  devices?: string[];
  platforms?: string[];
  search_terms?: string[];
  tags?: string[];
}) => ({
  countries,
  devices,
  platforms,
  search_terms,
  ...(tags.length > 0 ? { tags: tags.join(',') } : {}),
});

/**
 * The page-level queries POST their filters, like Brand Traffic: a long search term selection overflows the
 * URL (the API's gunicorn rejects request lines over 4094 bytes).
 */
export const nonBrandPerformanceRequest = (
  endpoint: string,
  { workspace_id, start_date, end_date, start_compare, end_compare, compare, opportunity, ...filters }: NonBrandPerformanceArgs
) => ({
  url: `/non-branded/search_terms/${endpoint}`,
  method: 'POST',
  body: {
    workspace_id,
    start_date,
    end_date,
    ...(compare && start_compare && end_compare ? { start_compare, end_compare, compare: true } : { compare: false }),
    ...(opportunity ? { opportunity } : {}),
    ...buildFilterBody(filters),
  },
});

/** Metric evolution modal: its own range and comparison, bucketed per week or month. */
export type NonBrandMetricEvolutionArgs = NonBrandPerformanceArgs & { timestep: 'year_week' | 'year_month' };

export type SearchTermsGridArgs = SearchTermsFiltersQueryWithSearchTerms &
  PaginateQuery & {
    filterModel?: GridFilterModel['items'];
    opportunity?: string | null;
    sort_field?: string;
    sort_order?: string;
    interval?: string;
    start_compare?: string;
    end_compare?: string;
    compare?: boolean;
  };

/** POSTed like the page-level queries: the grid gets the same search term selection. */
export const searchTermsGridRequest = (query: SearchTermsGridArgs) => ({
  url: '/non-branded/search_terms/grid',
  method: 'POST',
  body: {
    workspace_id: query.workspace_id || undefined,
    start_date: query.start_date || undefined,
    end_date: query.end_date || undefined,
    limit: query.limit || 10,
    page: query.page || 0,
    // The JSON string the API parses (former query param)
    ...(query.filterModel ? { filter_model: JSON.stringify(query.filterModel) } : {}),
    ...(query.opportunity ? { opportunity: query.opportunity } : {}),
    ...(query.sort_field ? { sort_field: query.sort_field, sort_order: query.sort_order } : {}),
    ...(query.start_compare ? { start_compare: query.start_compare } : {}),
    ...(query.end_compare ? { end_compare: query.end_compare } : {}),
    compare: !!query.compare,
    ...buildFilterBody(query),
  },
});

const nonBrandedAPI = createApi({
  baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL, credentials: 'include' }),
  reducerPath: 'api.nonBranded',
  tagTypes: ['nonBranded'],
  endpoints: (builder) => ({
    searchTermsFilters: builder.query<NonBrandSearchTermOption[], SearchTermsFiltersQuery>({
      query: ({ workspace_id, start_date, end_date, ...rest }) => ({
        url: `/non-branded/filters?workspace_id=${workspace_id}&start_date=${start_date}&end_date=${end_date}&${buildFilterQueryString(rest)}`,
        method: 'GET',
      }),
    }),
    nonBrandPerformanceKpis: builder.query<NonBrandKpisResponse, NonBrandPerformanceArgs>({
      query: (args) => nonBrandPerformanceRequest('non-brand-performance-kpis', args),
    }),
    nonBrandTrafficTimeSeries: builder.query<NonBrandTrafficTimeSeriesResponse, NonBrandPerformanceArgs>({
      query: (args) => nonBrandPerformanceRequest('non-brand-traffic-time-series', args),
    }),
    nonBrandMetricEvolution: builder.query<NonBrandMetricEvolutionResponse, NonBrandMetricEvolutionArgs>({
      query: ({ timestep, ...args }) => {
        const request = nonBrandPerformanceRequest('metric-evolution', args);
        return { ...request, body: { ...request.body, timestep } };
      },
    }),
    nonBrandPlatforms: builder.query<{ platforms: string[] }, { workspace_id: number; start_date: string; end_date: string }>({
      query: ({ workspace_id, start_date, end_date }) => ({
        url: `/non-branded/search_terms/platforms?workspace_id=${workspace_id}&start_date=${start_date}&end_date=${end_date}`,
        method: 'GET',
      }),
    }),
    searchTermsGrid: builder.query<
      SearchTermsGridResponse,
      SearchTermsGridArgs
    >({
      query: (query) => searchTermsGridRequest(query),
    }),
    clicksAdCosts: builder.query<
      ClicksAdCostsResponse,
      SearchTermsFiltersQueryWithSearchTerms & {
        interval?: string;
        compare?: boolean;
        compare_start_date?: string;
        compare_end_date?: string;
      }
    >({
      query: (query) => {
        let url = '/non-branded/search_terms/clicks_ad_costs' + (query.compare ? '/compare' : '');
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        if (query.search_terms.length > 0) {
          params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        const interval = query.interval ? `&interval=${query.interval}` : '';
        const compare_start_date = query.compare_start_date
          ? `&compare_start_date=${query.compare_start_date}`
          : '';
        const compare_end_date = query.compare_end_date
          ? `&compare_end_date=${query.compare_end_date}`
          : '';
        return {
          url:
            url +
            '?' +
            params.join('&') +
            workspace_id +
            start_date +
            end_date +
            interval +
            compare_start_date +
            compare_end_date,
          method: 'GET',
        };
      },
    }),
    visibility: builder.query<
      VisibilityResponse,
      SearchTermsFiltersQueryWithSearchTerms & { interval?: string; compare?: boolean; compare_start_date?: string; compare_end_date?: string }
    >({
      query: (query) => {
        let url = '/non-branded/search_terms/visibility' + (query.compare ? '/compare' : '');
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        if (query.search_terms.length > 0) {
          params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        const interval = query.interval ? `&interval=${query.interval}` : '';
        const compare_start_date = query.compare_start_date ? `&compare_start_date=${query.compare_start_date}` : '';
        const compare_end_date = query.compare_end_date ? `&compare_end_date=${query.compare_end_date}` : '';
        return {
          url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval + compare_start_date + compare_end_date,
          method: 'GET',
        };
      },
    }),
    topCompet: builder.query<
      TopCompetitorsResponse,
      SearchTermsFiltersQueryWithSearchTerms & { interval?: string }
    >({
      query: (query) => {
        let url = '/non-branded/search_terms/top_compet';
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        const interval = query.interval ? `&interval=${query.interval}` : '';
        return {
          url: url + '?' + params.join('&') + workspace_id + start_date + end_date + interval,
          method: 'GET',
        };
      },
    }),
    regions: builder.query<RegionsResponse, SearchTermsFiltersQueryWithSearchTerms>({
      query: (query) => {
        let url = '/non-branded/search_terms/regions';
        let params = [];
        if (query.countries.length > 0) {
          params.push(
            `countries=${query.countries.map((country) => country.replace('&', '__et__')).join('&countries=')}`
          );
        }
        if (query.devices.length > 0) {
          params.push(`devices=${query.devices.join('&devices=')}`);
        }
        if (query.platforms.length > 0) {
          params.push(`platforms=${query.platforms.join('&platforms=')}`);
        }
        if (query.search_terms.length > 0) {
          params.push(`search_terms=${query.search_terms.join('&search_terms=')}`);
        }
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        const start_date = query.start_date ? `&start_date=${query.start_date}` : '';
        const end_date = query.end_date ? `&end_date=${query.end_date}` : '';
        return {
          url: url + '?' + params.join('&') + workspace_id + start_date + end_date,
          method: 'GET',
        };
      },
    }),
    competitors: builder.query<any, CompetitorsFiltersQuery & { query: string, domain_name?: string }>({
      query: ({ query, filter_model, ...filters }) => {
        return {
          url: '/non-branded/competitors/' + query,
          method: 'POST',
          params: filters,
          body: { filter_model },
        };
      },
    }),
    // CONQUEST MODE
    conquestMode: builder.query<
      any,
      {
        workspace_id?: number | string;
        client_id: string;
        region_name: string[];
        period: 'l7d' | 'l14d' | 'l30d' | 'l90d';
        comparison: 'none' | 'pp' | 'ly';
      }
    >({
      query: ({
        workspace_id,
        client_id,
        region_name,
        period,
        comparison,
      }) => {
        const url = '/non-branded/conquest-mode/';
        const params: string[] = [];

        if (workspace_id) {
          params.push(`workspace_id=${workspace_id}`);
        }

        return {
          url: url + (params.length > 0 ? `?${params.join('&')}` : ''),
          method: 'POST',
          body: {
            client_id,
            region_name,
            period,
            comparison,
          },
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };
      },
    }),

    addConquestMode: builder.mutation<any, { workspace_id: number; data: any[] }>({
      query: ({ workspace_id, data }) => ({
        url: `/non-branded/conquest-mode/add?workspace_id=${workspace_id}`,
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
    }),
    disableConquestMode: builder.mutation<
      { success: boolean; disabled_ids: number[] },
      { ids: number[]; workspace_id: number }
    >({
      query: ({ ids, workspace_id }) => ({
        url: `/non-branded/conquest-mode/disable?workspace_id=${workspace_id}`,
        method: 'POST',
        body: ids.map(id => ({ id })),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
    }),
    updateConquestMode: builder.mutation<any, { workspace_id: any; data: any[] }>({
      query: ({ workspace_id, data }) => ({
        url: `/non-branded/conquest-mode/update?workspace_id=${workspace_id}`,
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }),
    }),
    regionsConquestMode: builder.query<RegionsResponse, any>({
      query: (query) => {
        let url = '/non-branded/conquest-mode/regions';
        let params: any = [];
        const workspace_id = query.workspace_id ? `&workspace_id=${query.workspace_id}` : '';
        return {
          url: url + '?' + params.join('&') + workspace_id,
          method: 'GET',
        };
      },
    }),
    analysisChartConquestMode: builder.query<
      {
        dates: string[];
        series: {
          name: string;
          data: number[];
          compare_data?: number[];
        }[];
      },
      {
        workspace_id?: number | string;
        client_id: string;
        keywords: string[];
        region_name: string[];
        period: 'l7d' | 'l14d' | 'l30d' | 'l90d';
        comparison: 'none' | 'pp' | 'ly';
        metrics: string[];
        granularity: 'day' | 'week' | 'month';
      }
    >({
      query: ({
        workspace_id,
        client_id,
        keywords,
        region_name,
        period,
        comparison,
        metrics,
        granularity,
      }) => {
        const params: string[] = [];

        if (workspace_id) {
          params.push(`workspace_id=${workspace_id}`);
        }

        return {
          url:
            '/non-branded/conquest-mode/analysis-chart' +
            (params.length > 0 ? `?${params.join('&')}` : ''),
          method: 'POST',
          body: {
            client_id,
            region_name,
            period,
            keywords,
            comparison,
            metrics,
            granularity,
          },
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };
      },
    }),
  }),
});

export const {
  useSearchTermsFiltersQuery,
  useNonBrandPerformanceKpisQuery,
  useNonBrandTrafficTimeSeriesQuery,
  useNonBrandMetricEvolutionQuery,
  useNonBrandPlatformsQuery,
  useSearchTermsGridQuery,
  useClicksAdCostsQuery,
  useVisibilityQuery,
  useTopCompetQuery,
  useRegionsQuery,
  useCompetitorsQuery,
  useConquestModeQuery,
  useAddConquestModeMutation,
  useDisableConquestModeMutation,
  useUpdateConquestModeMutation,
  useLazyCompetitorsQuery,
  useRegionsConquestModeQuery,
  useAnalysisChartConquestModeQuery
} = nonBrandedAPI;
export default nonBrandedAPI;